"use client"

import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { api } from "../services/api"
import { getApiUrl } from "../config/urls"
import { useSystemSettings } from "../contexts/SystemSettingsContext"
import { Upload, message } from 'antd';
import { UploadOutlined, FileImageOutlined } from '@ant-design/icons';

function Register() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "client",
    userType: "client",
    phone: "",
    circulation_permit: "",
    cin_number: "",
    insurance_certificate: "",
    truck_type: "",
    tunisian_serial_number: "",
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [fileList, setFileList] = useState({
    cin_photo: [],
    insurance_photo: [],
    registration_photo: []
  });
  const navigate = useNavigate()
  const { settings } = useSystemSettings()
  const registrationClosed = settings?.registrationEnabled === false

  const handleFileChange = ({ fileList: newFileList }, name) => {
    setFileList(prev => ({
      ...prev,
      [name]: newFileList
    }));
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Vous ne pouvez télécharger que des fichiers image!');
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('L\'image doit faire moins de 5MB!');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
    setError("")
  }

  const validateForm = () => {
    if (!formData.first_name || !formData.last_name) {
      setError("Veuillez fournir votre prénom et votre nom de famille")
      return false
    }
    
    if (!formData.email) {
      setError("Veuillez fournir une adresse email valide")
      return false
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas")
      return false
    }
    
    if (formData.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères")
      return false
    }
    
    if (formData.userType === "demenageur") {
      const requiredFields = {
        phone: "numéro de téléphone",
        circulation_permit: "permis de circulation",
        cin_number: "numéro de CIN",
        insurance_certificate: "certificat d'assurance",
        truck_type: "type de camion",
        tunisian_serial_number: "numéro de série tunisien"
      }
      
      for (const [field, fieldName] of Object.entries(requiredFields)) {
        if (!formData[field]?.trim()) {
          setError(`Veuillez fournir votre ${fieldName}`)
          return false
        }
      }
    }
    
    return true
  }

  // Fonction pour téléverser un fichier
  const uploadFile = async (file, type) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append('document', file);
    formData.append('type', type);
    
    try {
      const response = await fetch(`${getApiUrl()}/upload/document`, {
        method: 'POST',
        // Pas besoin du header Authorization pour le téléversement initial
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Échec du téléversement du fichier');
      }
      
      const result = await response.json();
      return result.url; // Retourne l'URL du fichier téléversé
    } catch (error) {
      console.error(`Erreur lors du téléversement du fichier ${type}:`, error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (registrationClosed) {
      setError("Les inscriptions sont temporairement fermées.")
      return
    }

    if (!validateForm()) return

    setIsLoading(true)
    setError("")

    try {
      // D'abord, téléverser les fichiers
      let cinPhotoUrl, insurancePhotoUrl, registrationPhotoUrl;
      
      if (formData.role === "demenageur") {
        try {
          // Téléverser chaque fichier s'il existe
          if (fileList.cin_photo?.length > 0) {
            cinPhotoUrl = await uploadFile(fileList.cin_photo[0].originFileObj, 'cin');
          }
          if (fileList.insurance_photo?.length > 0) {
            insurancePhotoUrl = await uploadFile(fileList.insurance_photo[0].originFileObj, 'insurance');
          }
          if (fileList.registration_photo?.length > 0) {
            registrationPhotoUrl = await uploadFile(fileList.registration_photo[0].originFileObj, 'registration');
          }
        } catch (uploadError) {
          console.error('Erreur lors du téléversement des fichiers:', uploadError);
          setError('Une erreur est survenue lors du téléversement des fichiers. Veuillez réessayer.');
          setIsLoading(false);
          return;
        }
      }

      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        phone: formData.phone || null,
        ...(formData.role === "demenageur"
          ? {
              siret: formData.siret || null,
              insurance_attestations: formData.insurance_attestations || null,
              company_name: formData.company_name || null,
              truck_types: formData.truck_types
                ? formData.truck_types
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : null,
              bio: formData.bio || null,
              rating: formData.rating ? Number(formData.rating) : null,
              circulation_permit: formData.circulation_permit || null,
              cin_number: formData.cin_number || null,
              insurance_certificate: formData.insurance_certificate || null,
              truck_type: formData.truck_type || null,
              tunisian_serial_number: formData.tunisian_serial_number || null,
              permits: formData.permits || null,
              // Ajouter les URLs des fichiers téléversés
              cin_photo_url: cinPhotoUrl || null,
              insurance_photo_url: insurancePhotoUrl || null,
              registration_photo_url: registrationPhotoUrl || null
            }
          : {}),
      }

      const data = await api.register(userData)
      localStorage.setItem("token", data.token)
      localStorage.setItem("user", JSON.stringify(data.user))
      localStorage.setItem("userType", data.user.role)

      switch (data.user.role) {
        case "admin":
          navigate("/admin")
          break
        case "client":
          navigate("/client")
          break
        case "demenageur":
          navigate("/demenageur")
          break
        default:
          navigate("/")
      }
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error)
      setError(error.message || "Une erreur est survenue lors de l'inscription")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-amber-500/30 transform transition-transform hover:scale-105">
            <span className="text-3xl">🚚</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Créer votre compte</h1>
          <p className="text-gray-400 text-sm">Rejoignez Liberty et simplifiez vos déménagements</p>
        </div>

        <div className="bg-gray-800/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl border border-gray-700/50 shadow-xl">
          {registrationClosed && (
            <p className="mb-5 p-4 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm text-center">
              Les inscriptions sont temporairement fermées. Réessayez plus tard ou contactez le support.
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Type de compte <span className="text-amber-400">*</span></label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "client", label: "Client", icon: "👤", desc: "Je veux déménager" },
                  { value: "demenageur", label: "Déménageur", icon: "🚚", desc: "Je propose mes services" },
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, userType: type.value, role: type.value })}
                    className={`p-3 rounded-xl border-2 transition-all duration-200 flex flex-col ${
                      formData.userType === type.value
                        ? "border-amber-400 bg-amber-500/10 text-amber-400 shadow-lg shadow-amber-400/10"
                        : "border-gray-700 hover:border-amber-400/40 hover:bg-gray-700/60 text-gray-300"
                    }`}
                  >
                    <span className="text-2xl mb-1.5">{type.icon}</span>
                    <span className="font-semibold text-sm">{type.label}</span>
                    <span className="text-xs text-gray-400 mt-0.5">{type.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5">
              <div className="space-y-1.5">
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-300">
                  Prénom <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700/60 border border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 outline-none"
                    placeholder="Votre prénom"
                  />
                  <div className="absolute inset-0 rounded-xl border border-transparent group-hover:border-amber-400/30 pointer-events-none transition-colors duration-200"></div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-300">
                  Nom de famille <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700/60 border border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 outline-none"
                    placeholder="Votre nom de famille"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Adresse email <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700/60 border border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 outline-none"
                    placeholder="votre@email.com"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                  Téléphone <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700/60 border border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 outline-none"
                    placeholder="+216 XX XXX XXX"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                </div>
              </div>

              {formData.userType === "demenageur" && (
                <div className="space-y-6">
                  <div className="border-t border-gray-700 pt-6">
                    <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Informations professionnelles
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                        <label htmlFor="circulation_permit" className="block text-sm font-medium text-amber-400 mb-2">
                          Permis de circulation *
                        </label>
                        <input
                          id="circulation_permit"
                          name="circulation_permit"
                          type="text"
                          value={formData.circulation_permit}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-700/30 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-white placeholder-gray-500 transition-colors duration-300"
                          placeholder="N° de permis"
                        />
                      </div>

                      <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                        <label htmlFor="cin_number" className="block text-sm font-medium text-amber-400 mb-2">
                          Numéro CIN *
                        </label>
                        <input
                          id="cin_number"
                          name="cin_number"
                          type="text"
                          value={formData.cin_number}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-700/30 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-white placeholder-gray-500 transition-colors duration-300"
                          placeholder="Ex : 12345678"
                        />
                      </div>

                      <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                        <label htmlFor="insurance_certificate" className="block text-sm font-medium text-amber-400 mb-2">
                          Certificat d'assurance *
                        </label>
                        <input
                          id="insurance_certificate"
                          name="insurance_certificate"
                          type="text"
                          value={formData.insurance_certificate}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-700/30 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-white placeholder-gray-500 transition-colors duration-300"
                          placeholder="N° de certificat"
                        />
                      </div>

                      <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                        <label htmlFor="truck_type" className="block text-sm font-medium text-amber-400 mb-2">
                          Type de camion *
                        </label>
                        <select
                          id="truck_type"
                          name="truck_type"
                          value={formData.truck_type}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-700/30 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-white placeholder-gray-500 transition-colors duration-300 appearance-none"
                        >
                          <option value="">Sélectionnez un type</option>
                          <option value="fourgon_10m3">Fourgon 10m³</option>
                          <option value="fourgon_20m3">Fourgon 20m³</option>
                          <option value="camion_30m3">Camion 30m³</option>
                          <option value="camion_40m3">Camion 40m³</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>

                      <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                        <label htmlFor="tunisian_serial_number" className="block text-sm font-medium text-amber-400 mb-2">
                          Immatriculation *
                        </label>
                        <input
                          id="tunisian_serial_number"
                          name="tunisian_serial_number"
                          type="text"
                          value={formData.tunisian_serial_number}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-700/30 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-white placeholder-gray-500 transition-colors duration-300"
                          placeholder="Ex : 1234TUN12"
                        />
                      </div>

                      <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                        <label htmlFor="experience_years" className="block text-sm font-medium text-amber-400 mb-2">
                          Années d'expérience *
                        </label>
                        <input
                          id="experience_years"
                          name="experience_years"
                          type="number"
                          min="0"
                          max="50"
                          className="w-full px-4 py-3 bg-gray-700/30 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-white placeholder-gray-500 transition-colors duration-300"
                          placeholder="Nombre d'années"
                        />
                      </div>

                      {/* Téléchargement des documents */}
                      <div className="md:col-span-2 bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                        <h4 className="text-sm font-medium text-amber-400 mb-3">Documents à fournir *</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-400 mb-2">Photo de la CIN</label>
                            <Upload
                              listType="picture-card"
                              fileList={fileList.cin_photo}
                              onChange={(info) => handleFileChange(info, 'cin_photo')}
                              beforeUpload={beforeUpload}
                              accept="image/*"
                              maxCount={1}
                              className="w-full"
                            >
                              {fileList.cin_photo.length >= 1 ? null : (
                                <div className="flex flex-col items-center justify-center p-2">
                                  <FileImageOutlined className="text-2xl text-amber-400 mb-1" />
                                  <span className="text-xs text-gray-300">Télécharger</span>
                                </div>
                              )}
                            </Upload>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-400 mb-2">Attestation d'assurance</label>
                            <Upload
                              listType="picture-card"
                              fileList={fileList.insurance_photo}
                              onChange={(info) => handleFileChange(info, 'insurance_photo')}
                              beforeUpload={beforeUpload}
                              accept="image/*"
                              maxCount={1}
                              className="w-full"
                            >
                              {fileList.insurance_photo.length >= 1 ? null : (
                                <div className="flex flex-col items-center justify-center p-2">
                                  <FileImageOutlined className="text-2xl text-amber-400 mb-1" />
                                  <span className="text-xs text-gray-300">Télécharger</span>
                                </div>
                              )}
                            </Upload>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-400 mb-2">Carte grise</label>
                            <Upload
                              listType="picture-card"
                              fileList={fileList.registration_photo}
                              onChange={(info) => handleFileChange(info, 'registration_photo')}
                              beforeUpload={beforeUpload}
                              accept="image/*"
                              maxCount={1}
                              className="w-full"
                            >
                              {fileList.registration_photo.length >= 1 ? null : (
                                <div className="flex flex-col items-center justify-center p-2">
                                  <FileImageOutlined className="text-2xl text-amber-400 mb-1" />
                                  <span className="text-xs text-gray-300">Télécharger</span>
                                </div>
                              )}
                            </Upload>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Formats acceptés : JPG, PNG (max 5MB)</p>
                      </div>
                    </div>
                  </div>

                  
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Mot de passe <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700/60 border border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 outline-none pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-400 hover:text-amber-400 focus:outline-none transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères, avec des chiffres et des lettres</p>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                  Confirmer le mot de passe <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700/60 border border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500 outline-none pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-400 hover:text-amber-400 focus:outline-none transition-colors duration-200"
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl flex items-start">
                <svg
                  className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium">Erreur</p>
                  <p className="text-sm opacity-90">{error}</p>
                </div>
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading || registrationClosed}
                className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl text-base font-semibold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-amber-500/20 ${
                  isLoading || registrationClosed ? 'opacity-80 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Création en cours...
                  </>
                ) : (
                  'Créer mon compte'
                )}
              </button>
              <p className="mt-5 text-center text-sm text-gray-400">
                Vous avez déjà un compte ?{' '}
                <Link to="/login" className="text-amber-400 hover:text-amber-300 font-medium transition-colors duration-200 hover:underline">
                  Se connecter
                </Link>
              </p>
            </div>
          </form>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors duration-200 group"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 transform -translate-x-0.5 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Register
